// Product.jsx
import React, { useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { TableHead, Th } from '../../Elements/DataTable';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { FaTh, FaList } from 'react-icons/fa';
import { UserContext } from "../../../context/UserContext";
import apiFetch, { safeJson } from '../../../context/api';
import ButtonBar, { primarioDelPrimero } from '../../Elements/ButtonBar';
import Button from '../../Elements/Button';
import BulkActionsBar from '../../Elements/BulkActionsBar';
import ProductModal from './ProductModal';
import ConfirmDialog from '../../Elements/ConfirmDialog';
import StockUpdateModal from './StockUpdateModal';
import StockLocationsModal from './StockLocationsModal';
import ProductGrid from './ProductGrid';
import ProductTable from './ProductTable';
import { canRetireProduct } from '../../../lib/helpers/products';
import useProducts from './useProducts';
import useMediaQuery from '../../hooks/useMediaQuery';
import { canEditStockLocations } from '../../../lib/helpers/permissions';
import './Products.css';

function uploadFileWithProgress({ url, file, method = 'PUT', headers = {}, onProgress }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
    });
    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve(xhr);
      else reject(new Error(`Upload failed: ${xhr.status}`));
    });
    xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
    xhr.open(method, url);
    Object.entries(headers).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.send(file);
  });
}

function applyLogFilter(rows, filter) {
  if (!rows) return [];
  return rows.filter(r => {
    if (filter === 'all') return true;
    if (filter === 'created') return r.status === 'created' && (!r.warnings || r.warnings.length === 0);
    if (filter === 'updated') return r.status === 'updated';
    if (filter === 'warning') return (r.warnings && r.warnings.length > 0);
    if (filter === 'error') return r.status === 'error';
    return true;
  });
}

function downloadFilteredCSV(importLog, filter) {
  const rows = applyLogFilter(importLog.rows, filter);
  const filterLabel = { all: 'todas', created: 'creados', updated: 'actualizados', warning: 'avisos', error: 'errores' }[filter] || filter;
  const headers = ['Fila', 'Estado', 'Nombre', 'Categoría', 'Avisos', 'Error'];
  const escape = v => {
    if (v == null) return '';
    const s = String(v).replace(/"/g, '""');
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s}"` : s;
  };
  const lines = [
    headers.join(','),
    ...rows.map(r => [
      r.row ?? '',
      r.status ?? '',
      r.name ?? '',
      r.category ?? '',
      (r.warnings || []).join(' | '),
      r.error ?? '',
    ].map(escape).join(',')),
  ];
  const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `import-log-${importLog.log_id}-${filterLabel}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function normalizeImportLogPayload(data, fallbackId) {
  if (!data || data.error) return data;
  if (Array.isArray(data)) {
    return {
      log_id: fallbackId,
      rows: data,
      created_count: 0,
      updated_count: 0,
      warning_count: 0,
      error_count: data.filter(r => r.status === 'error').length,
      total_rows: data.length,
    };
  }
  return {
    ...data,
    log_id: data.log_id || fallbackId,
  };
}

const Product = () => {
  const { pathname } = useLocation();
  const isAlmacenPage = pathname === '/almacen';
  const isCartaPage = pathname === '/carta';
  const isReadOnlyPage = isCartaPage;
  const [token, , ownedShops, venueID, , , email, accessPrivileges, , , , , , , canManageProducts] = useContext(UserContext);
  // Quién puede editar/borrar productos y categorías: OWNER/SUPERADMIN siempre,
  // STAFF solo si tiene el permiso can_manage_products.
  const _role = (accessPrivileges || '').toLowerCase();
  const canEditProducts = _role === 'owner' || _role === 'superadmin' || (_role === 'staff' && !!canManageProducts);
  // Importar productos por ZIP va por las mismas rutas que importar socios
  // (`/v1/clients/import-jobs`), y desde el 13-09 son solo owner y superadmin
  // (BE #637). Un staff con `can_manage_products` nunca llegó a completar una
  // importación —el worker ya exigía owner—, pero veía el botón y se comía un 403.
  const puedeImportarZip = _role === 'owner' || _role === 'superadmin';
  // …pero el MAPA de ubicaciones no va con el permiso de productos: es estructura
  // del club (owner/superadmin). El staff con can_manage_products veía el botón
  // "Ubicaciones" y al crear o borrar se comía un 403 del backend.
  const canEditLocations = canEditStockLocations(accessPrivileges);
  const [activeModal, setActiveModal] = useState(false);
  const [productId, setProductId] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [showEditCategoryModal, setShowEditCategoryModal] = useState(false);
  // Confirmaciones de la pantalla: borrar producto, retirarlo, borrado en lote, borrar
  // categoría y cerrar el panel de importación. Una sola caja de estado — `accion` es lo que
  // se ejecuta al decir que sí. Antes eran cinco `window.confirm`, y en la tablet del club
  // esos diálogos se tragan: las cinco acciones se quedaban muertas y en silencio, incluido
  // el alta de producto que reportaron el 24-08 («da error pero no se sabe cuál»).
  const [confirmacion, setConfirmacion] = useState(null);
  const [showDeleteCategoryModal, setShowDeleteCategoryModal] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [categoryToEdit, setCategoryToEdit] = useState(null);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [categoryRefreshTrigger, setCategoryRefreshTrigger] = useState(0);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  const [searchInput, setSearchInput] = useState("");
  // Debounced copy used only for the SERVER-side fetch (search param + wide limit).
  // The instant client-side filter still uses searchInput, so typing stays
  // responsive while we avoid refetching on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [visibleProductIds, setVisibleProductIds] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [categories, setCategories] = useState([]);
  const isMd = useMediaQuery('(min-width: 768px)');

  // Import job states (same system as socios)
  const [lastImportLogId, setLastImportLogId] = useState(() => localStorage.getItem('lastProductImportLogId') || null);
  const [importLog, setImportLog] = useState(null);
  const [showLogModal, setShowLogModal] = useState(false);
  const [loadingLog, setLoadingLog] = useState(false);
  const [logFilter, setLogFilter] = useState('all');
  const [showImportProgressModal, setShowImportProgressModal] = useState(false);
  const [importPhase, setImportPhase] = useState('idle'); // idle | uploading | processing | done | error
  const [importJobStatus, setImportJobStatus] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [importStatusMessage, setImportStatusMessage] = useState('');
  const pollTimerRef = useRef(null);

  const importHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'x-venue-id': venueID,
  }), [token, venueID]);

  const clearImportPoll = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearImportPoll(), [clearImportPoll]);

  const handleCategoryChange = useCallback((cat) => setSelectedCategory(cat), []);

  // La píldora explícita SIEMPRE manda: el texto del buscador nunca la sustituye.
  // (Antes, un texto que coincidiera con una categoría — «CBD» — secuestraba el filtro
  // y con Prerolls activo se enseñaban los botes de CBD; la selección masiva sobre lo
  // visible convertía eso en borrados sobre la categoría equivocada.)
  // "Sin categoría" se resuelve en cliente sobre una carga ancha: el alta del BE mete
  // los sin-categoría en una categoría real «Uncategorized», y el __no_category__ del
  // servidor solo cubre las dos formas a partir de la rama BE de esta misma tanda —
  // pasar la píldora a servidor es un follow-up cuando ese despliegue esté en todas partes.
  const activeCategoryName =
    !selectedCategory || selectedCategory === 'All' || selectedCategory === 'Sin categoría'
      ? null
      : selectedCategory;

  // Recortado (el filtro cliente y el servidor deben ver el MISMO término) y con el
  // tope de longitud del endpoint (max_length=200): sin él, un pegote largo en el
  // buscador sería un 422 y la pantalla entera se quedaría en un banner de error.
  const searchText = debouncedSearch.trim().slice(0, 200);

  // Productos: exclude dispensario. Almacen: all products.
  const productListOptions = useMemo(() => ({
    // La gestión ve TODO, incluidos los retirados: es la única pantalla desde la que
    // se pueden reactivar. Las de venta (dispensario, POS, carta del socio) no lo piden.
    includeInactive: true,
    ...(isAlmacenPage ? {} : { excludeDispensario: true }),
    ...(activeCategoryName ? { categoryName: activeCategoryName } : {}),
    ...(searchText ? { searchText } : {}),
    ...(selectedCategory === 'Sin categoría' ? { forceWide: true } : {}),
  }), [isAlmacenPage, activeCategoryName, searchText, selectedCategory]);

  const { products, loading: productsLoading, errorMessage: productsError, deleteProduct, setProductActive, refreshProducts, page: productPage, hasMore: productHasMore, goToPage: goToProductPage } = useProducts(token, venueID, productListOptions);
  const loaded = !productsLoading;

  // Desglose de stock por ubicación (almacén principal / armarios / neveras) para
  // mostrarlo en cada producto. Una sola llamada al board trae todos los productos.
  const [stockByLoc, setStockByLoc] = useState({}); // product_id -> [{id,name,kind,qty}]
  const fetchBoard = useCallback(async () => {
    if (!venueID || !token) return;
    try {
      const res = await apiFetch(`/v1/stock/board?venue_id=${venueID}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return;
      const data = await safeJson(res);
      const locById = {};
      for (const l of (data.locations || [])) locById[String(l.id)] = l;
      const map = {};
      for (const p of (data.products || [])) {
        const arr = [];
        // DISPONIBLE por ubicación (físico − carritos abiertos); fallback al físico.
        for (const [locId, qty] of Object.entries(p.available_by_location ?? p.by_location ?? {})) {
          const q = Number(qty) || 0;
          if (q === 0) continue;
          const loc = locById[String(locId)];
          arr.push({ id: locId, name: loc?.name || '—', kind: loc?.kind || '', qty: q });
        }
        arr.sort((a, b) => (a.kind === 'principal' ? -1 : b.kind === 'principal' ? 1 : a.name.localeCompare(b.name)));
        map[String(p.id)] = arr;
      }
      setStockByLoc(map);
    } catch { /* no-op: el desglose es informativo */ }
  }, [venueID, token]);
  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  // Combine error messages
  useEffect(() => {
    if (productsError) {
      setErrorMessage(productsError);
    }
  }, [productsError]);

  // Fetch categories from server (shared across all child views)
  useEffect(() => {
    if (!token || !venueID) return;
    const fetchCategories = async () => {
      try {
        const { data } = await axios.get('/v1/categories', {
          headers: { Authorization: `Bearer ${token}`, ...(venueID && { 'x-venue-id': venueID }) }
        });
        setCategories(data);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };
    fetchCategories();
  }, [token, venueID, categoryRefreshTrigger]);

  // Derive unique categories from products.
  const categoriesFromProducts = useMemo(() => {
    const categoryMap = new Map();
    products.forEach(product => {
      if (product.category && product.category.id) {
        categoryMap.set(product.category.id, product.category);
      }
    });
    return Array.from(categoryMap.values());
  }, [products]);

  // Search handler - búsqueda automática
  const handleSearchInput = (e) => {
    setSearchInput(e.target.value);
  };

  // Products to display (filtered by search if active). Recortado como el término
  // que viajó al servidor: con «CBD » (espacio final) el servidor devolvía bien y
  // este filtro lo tiraba todo por exigir el espacio dentro del nombre.
  const displayProducts = useMemo(() => {
    const searchTerm = searchInput.trim().toLowerCase();
    if (!searchTerm) {
      return products;
    }
    return products.filter(product => {
      const name = product.name?.toLowerCase() || '';
      const category = product.category?.name?.toLowerCase() || '';
      const id = product.id?.toString().toLowerCase() || '';
      return name.includes(searchTerm) || category.includes(searchTerm) || id.includes(searchTerm);
    });
  }, [products, searchInput]);

  useEffect(() => {
    setSelectedProductIds((prev) => prev.filter((id) => products.some((product) => String(product.id) === String(id))));
  }, [products]);

  // Reset category filter and category list when venue changes so we don't show stale pills
  useEffect(() => {
    setSelectedCategory('All');
    setCategories([]);
  }, [venueID]);

  // Modal toggle and edit handler.
  const handleModal = () => {
    setActiveModal(!activeModal);
    if (!activeModal) {
      // ✅ Limpiar productId cuando se cierra el modal
      setProductId(null);
    }
  };
  const handleEdit = (id) => {
    setProductId(id);
    setActiveModal(true);
  };

  // Actualizar stock (entrar/ajustar/historial) — modal por producto
  const [stockProduct, setStockProduct] = useState(null);
  const [showLocations, setShowLocations] = useState(false);
  const handleStock = (id) => {
    const p = (products || []).find((x) => String(x.id) === String(id));
    if (p) setStockProduct(p);
  };



  // Delete handler: calls custom hook's delete function.
  const handleDelete = (id) => {
    const producto = products.find((p) => String(p.id) === String(id));
    setConfirmacion({
      title: producto?.name ? `¿Eliminar «${producto.name}»?` : '¿Eliminar este producto?',
      description:
        'Se borra de la tienda y no se puede deshacer. Si tiene ventas o comandas no se ' +
        'podrá borrar: te lo dirá y podrás usar «Retirar», que lo esconde sin perder nada.',
      tone: 'danger',
      confirmLabel: 'Eliminar',
      accion: async () => {
        // El motivo se pinta desde el RETORNO, no desde el `errorMessage` del hook: ese
        // se limpiaba aquí mismo justo después de llamar, y el efecto que lo recopia solo
        // salta cuando el string cambia — así que al segundo 409 idéntico el mensaje
        // desaparecía y el botón parecía no hacer nada.
        const { ok, detail } = await deleteProduct(id);
        setErrorMessage(ok ? "" : (detail || "No se pudo eliminar el producto"));
      },
    });
  };

  /**
   * Atajo del callejón sin salida: pone a cero cada ubicación con existencias y luego
   * retira la referencia. No hay endpoint nuevo — son dos llamadas que ya existen.
   *
   * Riesgo declarado: son N+1 llamadas y NO son atómicas. Si falla a mitad quedan
   * ubicaciones a cero y el producto todavía activo: un estado recuperable y visible
   * (se ve el stock a 0 y el botón de retirar sigue ahí), no corrupto. El follow-up si
   * duele es un `POST /v1/stock/zero-out`, nunca meterlo dentro de `update_product`:
   * que guardar una ficha mueva stock es justo la puerta que se cerró tras Kong.
   */
  const ponerACeroYRetirar = async (product) => {
    try {
      const { data } = await axios.get('/v1/stock/by-location', {
        params: { product_id: product.id, venue_id: venueID },
        headers: axiosHeaders,
      });
      const porUbicacion = data?.by_location || [];
      const suma = porUbicacion.reduce((acc, f) => acc + Number(f.quantity || 0), 0);
      const total = Number(data?.stock_quantity ?? suma);

      // El guard de retirar del backend mira `product.stock_quantity` (el TOTAL), no la
      // suma de ubicaciones, y `adjust-location` mueve el total por DELTA. Si hay brecha
      // —stock sin ubicar, el descuadre del cutover— poner las ubicaciones a cero deja
      // el total en la brecha y el retirar volvería a dar 409: existencias escritas a
      // cero de forma irreversible Y sin conseguir nada. Así que se comprueba ANTES de
      // tocar nada y no se empieza siquiera.
      if (Math.abs(total - suma) > 0.0005) {
        setErrorMessage(
          `«${product?.name}» tiene ${total} en total pero solo ${suma} repartidas por ` +
          'ubicaciones: hay existencias sin ubicar. Ábrelo en Actualizar stock → ' +
          'Ubicaciones y pulsa «Cuadrar total con lo contado», y luego retíralo.'
        );
        return;
      }

      const conExistencias = porUbicacion.filter((f) => Number(f.quantity) !== 0);
      for (const fila of conExistencias) {
        await axios.post('/v1/stock/adjust-location', {
          product_id: product.id,
          venue_id: venueID,
          location_id: fila.location_id,
          new_quantity: 0,
          concept: 'Baja de referencia (retirada)',
        }, { headers: axiosHeaders });
      }
      const ok = await setProductActive(product.id, false);
      if (!ok) {
        // Las existencias YA están a cero: la pantalla tiene que reflejarlo o el
        // mensaje contradice a la tarjeta, que seguiría enseñando la cantidad vieja.
        fetchBoard();
        setErrorMessage(
          `Las existencias de «${product?.name}» quedaron a cero, pero no se pudo retirar. ` +
          'Vuelve a pulsar Retirar.'
        );
        return;
      }
      setErrorMessage("");
      fetchBoard();
    } catch (err) {
      setErrorMessage(
        err?.response?.data?.detail ||
        `No se pudieron poner a cero las existencias de «${product?.name}».`
      );
    }
  };

  /**
   * Retirar / reactivar una referencia. Se confirma solo al RETIRAR: es el que cambia
   * lo que ve el club (desaparece de dispensario, productos y carta del socio);
   * reactivar es inocuo y no merece un diálogo.
   */
  const handleToggleActive = async (product) => {
    const retirando = product?.is_active !== false;
    // Solo se retira con el stock a CERO (decisión del club, 15-08): con género dentro,
    // esconder la referencia la deja invisible para el POS mientras cuenta en el
    // inventario → descuadre en el primer recuento. El backend lo rechaza igualmente
    // con un 409; esto es para no hacer ir y volver al camarero.
    if (retirando && !canRetireProduct(product)) {
      // El callejón sin salida que reportó el club: un producto con ventas NO se puede
      // borrar (rompería la contabilidad) y con stock > 0 tampoco se puede retirar
      // (regla del 15-ago) → ninguna acción posible. En vez de dejarlo ahí, se ofrece
      // hacer las dos cosas de un tirón: poner a cero cada ubicación (queda en el
      // ledger, con autor y concepto) y luego retirar.
      setConfirmacion({
        title: `«${product?.name}» todavía tiene ${product?.stock_quantity} en stock`,
        description:
          'Una referencia solo se retira con las existencias a cero. Puedo ponerlas a cero ' +
          'y retirarla en un paso.',
        tone: 'danger',
        note:
          `Se darán de baja las ${product?.stock_quantity} existencias contadas: es un ajuste ` +
          'de inventario y no se deshace solo. Queda en el historial con tu nombre y el ' +
          'concepto «Baja de referencia (retirada)». La ficha y el historial del producto ' +
          'se conservan.',
        confirmLabel: 'Poner a cero y retirar',
        accion: () => ponerACeroYRetirar(product),
      });
      return;
    }
    const aplicar = async () => {
      const ok = await setProductActive(product.id, retirando ? false : true);
      if (!ok) setErrorMessage("No se pudo cambiar el estado del producto");
    };
    if (!retirando) {
      await aplicar();
      return;
    }
    setConfirmacion({
      title: `¿Retirar «${product?.name}»?`,
      description:
        'Dejará de aparecer en dispensario, productos y en la carta del socio. No se borra: ' +
        'conserva su ficha y su historial, y puedes reactivarlo cuando vuelva la referencia.',
      confirmLabel: 'Retirar',
      accion: aplicar,
    });
  };

  const toggleSelectedProduct = (id) => {
    setSelectedProductIds((prev) =>
      prev.some((currentId) => String(currentId) === String(id))
        ? prev.filter((currentId) => String(currentId) !== String(id))
        : [...prev, id]
    );
  };

  const clearSelectedProducts = () => setSelectedProductIds([]);

  const selectVisibleProducts = () => {
    if (visibleProductIds && visibleProductIds.length > 0) {
      setSelectedProductIds(visibleProductIds);
    }
  };

  const handleBulkDelete = () => {
    if (!selectedProductIds.length) return;
    const totalCount = selectedProductIds.length;
    setConfirmacion({
      title: `¿Eliminar ${totalCount} producto${totalCount === 1 ? '' : 's'}?`,
      description: 'Se borran de la tienda y no se puede deshacer.',
      tone: 'danger',
      confirmLabel: `Eliminar ${totalCount}`,
      accion: () => ejecutarBulkDelete(totalCount),
    });
  };

  const ejecutarBulkDelete = async (totalCount) => {
    setIsBulkDeleting(true);
    const results = await Promise.allSettled(selectedProductIds.map((id) => deleteProduct(id)));
    // deleteProduct traga sus errores y devuelve {ok:false} — allSettled nunca ve un
    // 'rejected', así que contar solo esos reportaba «todo borrado» con la API caída.
    const fallidos = results.filter(
      (result) => result.status === 'rejected' || result.value?.ok === false
    );
    const failedCount = fallidos.length;

    setIsBulkDeleting(false);
    setSelectedProductIds([]);

    if (failedCount > 0) {
      // «X fallaron» no dice nada accionable. Los motivos vienen del 409 del backend
      // (ventas, comandas, movimientos de almacén, carrito abierto) y son casi siempre
      // el mismo, así que se agrupan en vez de repetirse N veces.
      const motivos = [...new Set(
        fallidos.map((r) => r.value?.detail).filter(Boolean)
      )];
      const porQue = motivos.length ? ` Motivo: ${motivos.join(' · ')}` : '';
      setErrorMessage(
        `Se eliminaron ${totalCount - failedCount} de ${totalCount}. ` +
        `${failedCount} no se pudieron borrar.${porQue}`
      );
      return;
    }

    setErrorMessage("");
  };

  // Funciones para manejar categorías
  const axiosHeaders = useMemo(() => ({
    Authorization: `Bearer ${token}`,
    ...(venueID && { 'x-venue-id': venueID })
  }), [token, venueID]);

  const handleSaveNewCategory = async () => {
    const name = newCategoryName.trim();
    if (!name || !token || !venueID) return;

    try {
      await axios.post(
        '/v1/categories',
        { name, parent_id: null },
        { headers: axiosHeaders }
      );
      setNewCategoryName("");
      setShowNewCategoryInput(false);
      // Recargar productos para actualizar categorías
      refreshProducts();
      // Trigger para recargar categorías en ProductGrid
      setCategoryRefreshTrigger(prev => prev + 1);
    } catch (err) {
      console.error('Failed to create category:', err);
      const errorMsg = err.response?.data?.detail || 'Error al crear la categoría';
      alert(errorMsg);
    }
  };

  const handleDeleteCategory = async (category) => {
    if (!token || !category) {
      console.error('No token or category provided', { token: !!token, category });
      alert('Error: No se pudo obtener la información de la categoría');
      return;
    }

    setConfirmacion({
      title: `¿Eliminar la categoría «${category.name}»?`,
      description: 'Los productos que la tenían se quedan sin categoría. No se puede deshacer.',
      tone: 'danger',
      confirmLabel: 'Eliminar categoría',
      accion: () => borrarCategoria(category),
    });
  };

  const borrarCategoria = async (category) => {
    try {
      console.log('Deleting category:', category);
      const deleteHeaders = {
        Authorization: `Bearer ${token}`,
        ...(venueID && { 'x-venue-id': venueID })
      };
      console.log('Delete headers:', deleteHeaders);
      const response = await axios.delete(`/v1/categories/${category.id}`, {
        headers: deleteHeaders
      });
      console.log('Delete response:', response);
      setCategoryToDelete(null);
      setShowDeleteCategoryModal(false);
      // Recargar productos para actualizar categorías
      refreshProducts();
      // Trigger para recargar categorías en ProductGrid
      setCategoryRefreshTrigger(prev => prev + 1);
      alert('Categoría eliminada exitosamente');
    } catch (err) {
      console.error("Error deleting category:", err);
      console.error("Error response:", err.response);
      const errorMsg = err.response?.data?.detail || err.message || 'Error al eliminar la categoría';
      alert(`Error: ${errorMsg}`);
      // No cerrar el modal si hay error para que el usuario pueda intentar de nuevo
    }
  };

  const handleRenameCategory = async (category, nextName) => {
    if (!token || !category || !nextName.trim()) {
      return;
    }

    try {
      await axios.patch(
        `/v1/categories/${category.id}`,
        { name: nextName.trim() },
        { headers: axiosHeaders }
      );
      setCategoryToEdit(null);
      setShowEditCategoryModal(false);
      refreshProducts();
      setCategoryRefreshTrigger(prev => prev + 1);
      alert('Categoría actualizada exitosamente');
    } catch (err) {
      console.error('Error renaming category:', err);
      const errorMsg = err.response?.data?.detail || 'Error al actualizar la categoría';
      alert(errorMsg);
    }
  };

  const openEditModal = async () => {
    try {
      const { data } = await axios.get('/v1/categories', {
        headers: axiosHeaders
      });
      if (data.length === 0) {
        alert('No hay categorías para editar');
        return;
      }
      setShowEditCategoryModal(true);
    } catch (err) {
      console.error('Error fetching categories:', err);
      alert('Error al cargar las categorías');
    }
  };

  const openDeleteModal = async () => {
    // Obtener categorías para el modal
    try {
      const { data } = await axios.get('/v1/categories', {
        headers: axiosHeaders
      });
      if (data.length === 0) {
        alert('No hay categorías para eliminar');
        return;
      }
      setShowDeleteCategoryModal(true);
    } catch (err) {
      console.error('Error fetching categories:', err);
      alert('Error al cargar las categorías');
    }
  };

  // Import job polling
  const startImportJobPolling = useCallback((jobId) => {
    clearImportPoll();
    const pollOnce = async () => {
      try {
        const res = await axios.get(`/v1/clients/import-jobs/${jobId}`, { headers: importHeaders() });
        const status = res.data;
        setImportJobStatus(status);
        if (status.status === 'completed') {
          clearImportPoll();
          setImportPhase('done');
          setImportStatusMessage(
            `Importación completada: ${status.created_count} productos` +
            (status.error_count ? `, ${status.error_count} errores` : '') +
            (status.warning_count ? `, ${status.warning_count} avisos` : '') + '.'
          );
          refreshProducts();
          setCategoryRefreshTrigger(prev => prev + 1);
        } else if (status.status === 'failed') {
          clearImportPoll();
          setImportPhase('error');
          setImportStatusMessage(status.error_message || 'La importación falló.');
        } else {
          const processed = (status.created_count || 0) + (status.error_count || 0);
          setImportStatusMessage(`Procesando productos… ${processed}`);
        }
      } catch (pollError) {
        console.warn('Import job poll failed:', pollError);
      }
    };
    pollOnce();
    pollTimerRef.current = setInterval(pollOnce, 3000);
  }, [clearImportPoll, importHeaders, refreshProducts]);

  // Bulk Upload Handler — uses same import jobs system as socios
  const handleBulkUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.zip')) {
      alert('Por favor, sube un archivo .zip');
      event.target.value = null;
      return;
    }

    try {
      setErrorMessage('');
      clearImportPoll();
      setShowImportProgressModal(true);
      setImportPhase('uploading');
      setUploadProgress(0);
      setImportJobStatus(null);
      setImportStatusMessage('Preparando importación…');

      const createRes = await axios.post('/v1/clients/import-jobs', {
        filename: file.name,
        size_bytes: file.size,
        content_type: 'application/zip',
        job_type: 'products',
      }, { headers: importHeaders() });

      const { job_id: jobId, upload } = createRes.data;
      setLastImportLogId(jobId);
      localStorage.setItem('lastProductImportLogId', jobId);
      setImportStatusMessage('Subiendo archivo…');

      if (upload.mode === 's3') {
        await uploadFileWithProgress({
          url: upload.url,
          file,
          method: upload.method || 'PUT',
          headers: upload.headers || { 'Content-Type': 'application/zip' },
          onProgress: setUploadProgress,
        });
        setUploadProgress(100);
        await axios.post(`/v1/clients/import-jobs/${jobId}/confirm-upload`, null, { headers: importHeaders() });
      } else {
        const formData = new FormData();
        formData.append('file', file);
        await axios.post(upload.url, formData, {
          headers: importHeaders(),
          timeout: 10 * 60 * 1000,
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
            }
          },
        });
        setUploadProgress(100);
      }

      setImportPhase('processing');
      setImportStatusMessage('Archivo recibido. Procesando productos en segundo plano…');
      await axios.post(`/v1/clients/import-jobs/${jobId}/start`, null, { headers: importHeaders() });
      startImportJobPolling(jobId);
    } catch (error) {
      console.error('Bulk upload error:', error);
      clearImportPoll();
      setImportPhase('error');
      const msg = error.response?.data?.detail || error.message || 'Error en la carga masiva';
      setImportStatusMessage(msg);
      setErrorMessage(msg);
    } finally {
      event.target.value = null;
    }
  };

  const openImportLog = async () => {
    if (!lastImportLogId) return;
    setLoadingLog(true);
    setShowLogModal(true);
    setImportLog(null);
    setLogFilter('all');
    try {
      let res;
      try {
        res = await axios.get(`/v1/clients/import-jobs/${lastImportLogId}/log`, {
          headers: importHeaders(),
        });
      } catch (jobLogError) {
        if (jobLogError.response?.status === 404) {
          // Try legacy log endpoint as fallback
          try {
            res = await axios.get(`/v1/clients/import-logs/${lastImportLogId}`, {
              headers: importHeaders(),
            });
          } catch (legacyErr) {
            // Both endpoints returned 404 — log was lost (container restart wiped local files)
            setImportLog({ error: 'El log de esta importación ya no está disponible (el servidor fue reiniciado). Realiza una nueva importación para ver el resultado.' });
            setLoadingLog(false);
            return;
          }
        } else {
          throw jobLogError;
        }
      }
      setImportLog(normalizeImportLogPayload(res.data, lastImportLogId));
    } catch (e) {
      setImportLog({ error: e.response?.data?.detail || 'No se pudo cargar el log' });
    } finally {
      setLoadingLog(false);
    }
  };

  const closeImportProgressModal = () => {
    // Con `window.confirm` esto era peor que un aviso perdido: en la tablet devolvía `false`
    // y el panel de importación NO SE PODÍA CERRAR de ninguna manera.
    if (importPhase === 'processing') {
      setConfirmacion({
        title: '¿Cerrar el panel de importación?',
        description:
          'La importación sigue en segundo plano. Puedes ver el resultado con «Ver Log Import».',
        confirmLabel: 'Cerrar panel',
        cancelLabel: 'Seguir mirando',
        accion: () => {
          clearImportPoll();
          setShowImportProgressModal(false);
        },
      });
      return;
    }
    setShowImportProgressModal(false);
  };


  // Menu bar configuration
  const menu_bar = [
    {
      title: isAlmacenPage ? 'Almacen' : isCartaPage ? 'Carta' : 'Productos',
      buttons: isReadOnlyPage ? [] : [
        {
          label: 'Crear Producto',
          onClick: () => {
            setProductId(null);
            setActiveModal(true);
          },
        },
        ...(puedeImportarZip ? [{
          label: 'Subir ZIP',
          onClick: () => document.getElementById('bulk-upload-input').click(),
        }] : []),
        ...(puedeImportarZip && lastImportLogId ? [{ label: '📋 Ver Log Import', onClick: openImportLog }] : []),
        {
          label: 'Crear Categoría',
          onClick: () => setShowNewCategoryInput(true),
        },
        {
          label: 'Editar Categoría',
          onClick: openEditModal,
        },
        {
          label: 'Eliminar Categoría',
          color: 'red',
          onClick: openDeleteModal,
        },
      ],
      viewToggle: (
        <div className="flex gap-1 mr-2">
          <input
            type="file"
            id="bulk-upload-input"
            accept=".zip"
            style={{ display: 'none' }}
            onChange={handleBulkUpload}
          />
          {/* Conmutador de vista: el activo va en el primario del sistema, no en
              un azul propio (auditoría de UI, UI-004). */}
          <button
            onClick={() => setViewMode('cards')}
            aria-pressed={viewMode === 'cards'}
            className={`p-2 rounded-sm transition-colors ${viewMode === 'cards'
                ? 'bg-[var(--primary)] text-[var(--primary-foreground)]'
                : 'bg-[var(--muted)] text-[var(--muted-foreground)] hover:bg-[var(--secondary)]'
              }`}
            title="Vista de tarjetas"
          >
            <FaTh />
          </button>
          <button
            onClick={() => setViewMode('table')}
            aria-pressed={viewMode === 'table'}
            className={`p-2 rounded-sm transition-colors ${viewMode === 'table'
                ? 'bg-[var(--primary)] text-[var(--primary-foreground)]'
                : 'bg-[var(--muted)] text-[var(--muted-foreground)] hover:bg-[var(--secondary)]'
              }`}
            title="Vista de tabla"
          >
            <FaList />
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="wa-almacen-page">
      <div className="flex flex-col gap-3 sm:gap-4 max-w-full overflow-x-hidden">
        {menu_bar.map((menuItem, index) =>
          isAlmacenPage ? (
            <React.Fragment key={index}>
              {/* Desktop: usa el ButtonBar normal (solo ≥ lg) */}
              <div className="hidden xl:block">
                <ButtonBar
                  title={menuItem.title}
                  buttons={menuItem.buttons}
                  viewToggle={menuItem.viewToggle}
                />
              </div>
              {/* Móvil / Tablet: la misma tarjeta y los mismos botones que la
                  cabecera compacta de Socios (Customers.jsx). Antes era un «hero»
                  propio con insignia, sombra y botones a mano con sombra: un
                  segundo sistema visual solo por debajo de xl (auditoría de UI,
                  UI-004). Rejilla de 2 columnas con `Button` a ancho completo. */}
              <div className="block xl:hidden mb-4 space-y-3 rounded-md border border-[var(--border)] bg-[var(--card)] p-4">
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-xl font-semibold text-[var(--foreground)]">{menuItem.title}</h2>
                  {menuItem.viewToggle && (
                    <div className="flex items-center">
                      {menuItem.viewToggle}
                    </div>
                  )}
                </div>
                {/* Dos columnas mientras quepan (156 px por botón: «Eliminar
                    Categoría» mide 122 en Inter más 32 de padding, y `Button` no
                    envuelve el texto a propósito); en un móvil de 375 o menos,
                    una. En estilo inline y no como clase arbitraria: da igual para el
                    resultado, y no depende del JIT de Tailwind. (Al hacerse, la caché
                    de webpack compartida entre worktrees servía CSS viejo y parecía
                    que la clase no se generaba; era la caché, no el JIT.) */}
                <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(156px, 1fr))' }}>
                  {menuItem.buttons.map((button, i) => (
                    <Button
                      key={i}
                      variant={primarioDelPrimero(button, i)}
                      onClick={button.onClick}
                      disabled={button.disabled}
                      title={button.title}
                      className="w-full"
                    >
                      {button.label}
                    </Button>
                  ))}
                </div>
              </div>
            </React.Fragment>
          ) : (
            <ButtonBar
              key={index}
              title={menuItem.title}
              buttons={menuItem.buttons}
              viewToggle={menuItem.viewToggle}
            />
          )
        )}
      </div>

      {errorMessage && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{errorMessage}</span>
          <button
            className="absolute top-0 bottom-0 right-0 px-4 py-3"
            onClick={() => setErrorMessage("")}
          >
            <span className="text-red-700">×</span>
          </button>
        </div>
      )}

      {!isReadOnlyPage ? (
        <BulkActionsBar
          selectedCount={selectedProductIds.length}
          onClear={clearSelectedProducts}
          onSelectAll={selectVisibleProducts}
          onDelete={handleBulkDelete}
          deleteLabel="Eliminar productos"
          disabled={isBulkDeleting}
        />
      ) : null}

      {/* Buscador */}
      {isAlmacenPage ? (
        <div className="wa-almacen-hero p-4 sm:p-6 space-y-4">
          <span className="wa-almacen-badge">Inventario y surtido</span>
          <div className="space-y-2">
            <h2 className="wa-almacen-title">Almacén</h2>
            <p className="wa-almacen-subtitle">
              Gestiona stock, categorías y catálogo con la misma presentación limpia y editorial de la portada.
            </p>
          </div>
          <div className="flex items-center justify-between gap-3">
            <div className="w-full">
              <input
                className="wa-almacen-search text-sm focus:outline-none active:outline-none border border-gray-300 w-full h-12 px-4 rounded"
                onChange={handleSearchInput}
                placeholder="Buscar por: Nombre, Categoría o ID"
                value={searchInput}
              />
            </div>
            {!isReadOnlyPage && canEditLocations && (
              <button
                onClick={() => setShowLocations(true)}
                className="flex-shrink-0 rounded-lg border border-gray-300 bg-white px-4 h-12 text-sm font-semibold text-gray-700 hover:bg-gray-50"
              >
                Ubicaciones
              </button>
            )}
            <div className="hidden lg:flex items-center flex-shrink-0">
              {menu_bar[0]?.viewToggle}
            </div>
          </div>
        </div>
      ) : (
        <div className='bg-white justify-left shadow-2xl'>
          <div className="bg-white p-3 sm:p-4 rounded-lg flex items-center border border-gray-200 justify-between rounded">
            <input
              className="text-sm focus:outline-none active:outline-none border border-gray-300 w-full h-10 pl-11 pr-4 rounded"
              onChange={handleSearchInput}
              placeholder="Buscar por: Nombre, Categoría o ID"
              value={searchInput}
            />
          </div>
        </div>
      )}

      {isAlmacenPage ? (
        <div className="min-w-0">
          {viewMode === 'cards' ? (
            <div className="wa-almacen-panel p-3 sm:p-4 flex-auto min-w-0">
              <ProductGrid
                products={displayProducts}
                loading={!loaded}
                onEdit={handleEdit}
                onStock={handleStock}
                onDelete={handleDelete}
                onToggleActive={handleToggleActive}
              canEdit={canEditProducts}
                selectedIds={selectedProductIds}
                onToggleSelect={toggleSelectedProduct}
                token={token}
                refreshTrigger={categoryRefreshTrigger}
                readOnly={isReadOnlyPage}
                searchQuery={searchInput}
                onVisibleProductsChange={setVisibleProductIds}
                selectedCategory={selectedCategory}
                onCategoryChange={handleCategoryChange}
                categories={categories}
              stockByLoc={stockByLoc}
                landscapeCols={5}
              />
            </div>
          ) : (
            <ProductTable
              products={displayProducts}
              loading={!loaded}
              onEdit={handleEdit}
                onStock={handleStock}
              onDelete={handleDelete}
              onToggleActive={handleToggleActive}
              canEdit={canEditProducts}
              selectedIds={selectedProductIds}
              onToggleSelect={toggleSelectedProduct}
              token={token}
              venueID={venueID}
              refreshTrigger={categoryRefreshTrigger}
              readOnly={isReadOnlyPage}
              searchQuery={searchInput}
              onVisibleProductsChange={setVisibleProductIds}
              selectedCategory={selectedCategory}
              onCategoryChange={handleCategoryChange}
              categories={categories}
              stockByLoc={stockByLoc}
            />
          )}
        </div>
      ) : (
        <div className="min-w-0">
          {!isMd ? (
            <ProductGrid
              products={displayProducts}
              loading={!loaded}
              onEdit={handleEdit}
                onStock={handleStock}
              onDelete={handleDelete}
              onToggleActive={handleToggleActive}
              canEdit={canEditProducts}
              selectedIds={selectedProductIds}
              onToggleSelect={toggleSelectedProduct}
              token={token}
              refreshTrigger={categoryRefreshTrigger}
              readOnly={isReadOnlyPage}
              searchQuery={searchInput}
              onVisibleProductsChange={setVisibleProductIds}
              selectedCategory={selectedCategory}
              onCategoryChange={handleCategoryChange}
              categories={categories}
              stockByLoc={stockByLoc}
            />
          ) : viewMode === 'cards' ? (
            <div className="bg-white p-3 sm:p-4 rounded-lg shadow border border-gray-200 flex-auto min-w-0">
              <ProductGrid
                products={displayProducts}
                loading={!loaded}
                onEdit={handleEdit}
                onStock={handleStock}
                onDelete={handleDelete}
                onToggleActive={handleToggleActive}
              canEdit={canEditProducts}
                selectedIds={selectedProductIds}
                onToggleSelect={toggleSelectedProduct}
                token={token}
                refreshTrigger={categoryRefreshTrigger}
                readOnly={isReadOnlyPage}
                searchQuery={searchInput}
                onVisibleProductsChange={setVisibleProductIds}
                selectedCategory={selectedCategory}
                onCategoryChange={handleCategoryChange}
                categories={categories}
              stockByLoc={stockByLoc}
              />
            </div>
          ) : (
            <ProductTable
              products={displayProducts}
              loading={!loaded}
              onEdit={handleEdit}
                onStock={handleStock}
              onDelete={handleDelete}
              onToggleActive={handleToggleActive}
              canEdit={canEditProducts}
              selectedIds={selectedProductIds}
              onToggleSelect={toggleSelectedProduct}
              token={token}
              venueID={venueID}
              refreshTrigger={categoryRefreshTrigger}
              readOnly={isReadOnlyPage}
              searchQuery={searchInput}
              onVisibleProductsChange={setVisibleProductIds}
              selectedCategory={selectedCategory}
              onCategoryChange={handleCategoryChange}
              categories={categories}
              stockByLoc={stockByLoc}
            />
          )}
        </div>
      )}

      {/* Pagination */}
      {(productPage > 0 || productHasMore) && !searchInput && (
        <div className="flex items-center justify-between border-t border-gray-200 pt-3 mt-2 px-1">
          <span className="text-sm text-gray-500">
            Página {productPage + 1} · {products.length} productos
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => goToProductPage(productPage - 1)}
              disabled={productPage === 0 || productsLoading}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ← Anterior
            </button>
            <button
              onClick={() => goToProductPage(productPage + 1)}
              disabled={!productHasMore || productsLoading}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Siguiente →
            </button>
          </div>
        </div>
      )}

      {!isReadOnlyPage ? (
        <ProductModal
          active={activeModal}
          handleModal={handleModal}
          token={token}
          productId={productId}
          productData={productId ? products.find(p => p.id === productId) : null}
          ownedShops={ownedShops}
          email={email}
          setErrorMessage={setErrorMessage}
          onSaved={refreshProducts}
          // El alta nace a 0 y encadena con Entrar stock (pestaña por defecto del modal):
          // una sola puerta para el género nuevo (Kong, 25-09-2026). Solo a quien puede
          // entrar stock (POST /v1/stock/entry exige product_editor): a los demás no se
          // les abre una puerta que devuelve 403 — el modal les dice quién lo mete.
          onCreated={canEditProducts ? (creado) => { if (creado?.id) setStockProduct(creado); } : undefined}
          refreshTrigger={categoryRefreshTrigger}
          categories={categoriesFromProducts}
          onOpenStock={(id) => {
            const pid = id || productId;
            const p = (products || []).find((x) => String(x.id) === String(pid));
            setActiveModal(false);
            setProductId(null);
            if (p) setStockProduct(p);
          }}
        />
      ) : null}

      {/* Modal Actualizar Stock (entrar / ajustar / historial) */}
      {stockProduct && (
        <StockUpdateModal
          // Remontar por producto: su estado (pestaña, cantidad, destino) nace del producto.
          key={stockProduct.id}
          product={stockProduct}
          token={token}
          venueID={venueID}
          puedeCuadrarTotal={canEditLocations}
          onClose={() => setStockProduct(null)}
          onUpdated={() => { refreshProducts(); fetchBoard(); }}
        />
      )}

      {/* Modal Ubicaciones de stock (almacén / neveras / armarios) */}
      {showLocations && (
        <StockLocationsModal
          token={token}
          venueID={venueID}
          canEdit={canEditLocations}
          onClose={() => setShowLocations(false)}
        />
      )}

      {/* Modal para Crear Categoría */}
      {!isReadOnlyPage && showNewCategoryInput && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md my-4 mx-2 sm:mx-4">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-800">Crear Categoría</h2>
                <button
                  onClick={() => {
                    setShowNewCategoryInput(false);
                    setNewCategoryName("");
                  }}
                  className="text-gray-500 hover:text-gray-800 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
              <input
                type="text"
                placeholder="Nombre de la categoría"
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && newCategoryName.trim()) {
                    handleSaveNewCategory();
                  }
                }}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none mb-4"
                autoFocus
              />
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => {
                    setShowNewCategoryInput(false);
                    setNewCategoryName("");
                  }}
                  className="px-5 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-all"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveNewCategory}
                  disabled={!newCategoryName.trim()}
                  className="px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  Crear
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para Eliminar Categoría */}
      {!isReadOnlyPage && showEditCategoryModal && (
        <CategoryEditModal
          active={showEditCategoryModal}
          onClose={() => {
            setShowEditCategoryModal(false);
            setCategoryToEdit(null);
          }}
          onSave={handleRenameCategory}
          categoryToEdit={categoryToEdit}
          setCategoryToEdit={setCategoryToEdit}
          token={token}
          venueID={venueID}
        />
      )}

      {/* Import progress modal — same UX as Socios */}
      {showImportProgressModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <h2 className="text-lg font-bold text-gray-800">Importación masiva</h2>
              <button
                onClick={closeImportProgressModal}
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
              >×</button>
            </div>

            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">{importStatusMessage}</p>

              {importPhase === 'uploading' && (
                <div>
                  <div className="flex justify-between text-xs text-gray-500 mb-1">
                    <span>Subiendo ZIP</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-500 transition-all duration-200"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {importPhase === 'processing' && (
                <div className="space-y-2">
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-500 animate-pulse w-full" />
                  </div>
                  {importJobStatus && (
                    <div className="grid grid-cols-3 gap-2 text-center text-xs">
                      <div className="bg-green-50 rounded p-2">
                        <div className="font-bold text-green-800">{importJobStatus.created_count || 0}</div>
                        <div className="text-green-700">Creados</div>
                      </div>
                      <div className="bg-yellow-50 rounded p-2">
                        <div className="font-bold text-yellow-800">{importJobStatus.warning_count || 0}</div>
                        <div className="text-yellow-700">Avisos</div>
                      </div>
                      <div className="bg-red-50 rounded p-2">
                        <div className="font-bold text-red-800">{importJobStatus.error_count || 0}</div>
                        <div className="text-red-700">Errores</div>
                      </div>
                    </div>
                  )}
                  <p className="text-xs text-gray-500">
                    Puedes cerrar este panel; la importación continúa en segundo plano.
                    Usa &quot;Ver Log Import&quot; para ver el resultado.
                  </p>
                </div>
              )}

              {importPhase === 'done' && (
                <div className="rounded-lg bg-green-50 border border-green-200 p-4 text-sm text-green-800">
                  {importStatusMessage}
                </div>
              )}

              {importPhase === 'error' && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">
                  {importStatusMessage}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                {(importPhase === 'done' || importPhase === 'error') && lastImportLogId && (
                  <button
                    type="button"
                    onClick={() => {
                      setShowImportProgressModal(false);
                      openImportLog();
                    }}
                    className="px-4 py-2 bg-slate-700 hover:bg-slate-800 text-white rounded text-sm font-semibold"
                  >
                    Ver log
                  </button>
                )}
                <button
                  type="button"
                  onClick={closeImportProgressModal}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm font-semibold"
                >
                  {importPhase === 'processing' ? 'Cerrar panel' : 'Cerrar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Import log modal — same UX as Socios */}
      {showLogModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h2 className="text-lg font-bold text-gray-800">📋 Log de Importación</h2>
                {importLog && !importLog.error && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    {importLog.timestamp ? new Date(importLog.timestamp).toLocaleString('es-ES') : ''} · ID: {importLog.log_id}
                  </p>
                )}
              </div>
              <button
                onClick={() => setShowLogModal(false)}
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
              >×</button>
            </div>

            {loadingLog && (
              <div className="flex-1 flex items-center justify-center py-16 text-gray-500">
                Cargando log…
              </div>
            )}

            {!loadingLog && importLog?.error && (
              <div className="flex-1 p-6 text-red-600">
                Error: {importLog.error}
              </div>
            )}

            {!loadingLog && importLog && !importLog.error && (
              <>
                <div className="flex flex-wrap gap-3 p-4 border-b border-gray-100">
                  <span className="px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800">
                    ✅ Creados: {importLog.created_count}
                  </span>
                  {importLog.updated_count > 0 && (
                    <span className="px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-800">
                      ♻️ Actualizados: {importLog.updated_count}
                    </span>
                  )}
                  <span className="px-3 py-1 rounded-full text-sm font-semibold bg-yellow-100 text-yellow-800">
                    ⚠️ Avisos: {importLog.warning_count}
                  </span>
                  <span className="px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-800">
                    ❌ Errores: {importLog.error_count}
                  </span>
                  <span className="px-3 py-1 rounded-full text-sm font-semibold bg-gray-100 text-gray-700">
                    Total filas: {importLog.total_rows}
                  </span>
                </div>

                {(importLog.errors_summary || []).length > 0 && (
                  <div className="mx-4 mt-4 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-900">
                    <div className="font-semibold mb-1">Resumen de avisos</div>
                    {(importLog.errors_summary || []).slice(0, 5).map((msg, idx) => (
                      <p key={idx} className="mt-1">{msg}</p>
                    ))}
                  </div>
                )}

                <div className="flex gap-1 px-4 pt-3">
                  {[
                    { key: 'all', label: 'Todas' },
                    { key: 'created', label: 'Creados' },
                    { key: 'updated', label: 'Actualizados' },
                    { key: 'warning', label: 'Con avisos' },
                    { key: 'error', label: 'Errores' },
                  ].map(({ key, label }) => (
                    <button
                      key={key}
                      onClick={() => setLogFilter(key)}
                      className={`px-3 py-1.5 rounded text-xs font-semibold transition-colors ${
                        logFilter === key
                          ? 'bg-slate-700 text-white'
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>

                <div className="flex-1 overflow-y-auto px-4 py-3">
                  <table className="w-full text-xs">
                    {/* A sangre y compacta. Las FILAS no pasan por `Tr` — ver abajo. */}
                    <TableHead>
                      <Th className="w-10 py-2 pl-0 pr-2">#</Th>
                      <Th className="py-2 pl-0 pr-2">Estado</Th>
                      <Th className="py-2 pl-0 pr-2">Nombre</Th>
                      <Th className="py-2 pl-0 pr-2">Categoría</Th>
                      <Th className="px-0 py-2">Avisos / Error</Th>
                    </TableHead>
                    <tbody>
                      {applyLogFilter(importLog.rows, logFilter).map((row, i) => {
                        const hasWarning = row.warnings && row.warnings.length > 0;
                        const isError = row.status === 'error';
                        const isUpdated = row.status === 'updated';
                        return (
                          // Aquí el FONDO de la fila es el dato: dice si esa línea del
                          // CSV entró, se actualizó, avisó o falló. Por eso no pasa por
                          // `Tr`: el hover del sistema le pintaría encima justo cuando
                          // el operario la señala con el ratón para leerla.
                          <tr
                            key={i}
                            className={`border-b border-[var(--border)] ${
                              isError ? 'bg-red-50' : hasWarning ? 'bg-yellow-50' : isUpdated ? 'bg-blue-50' : ''
                            }`}
                          >
                            <td className="py-1.5 pr-2 text-[var(--muted-foreground)]">{row.row}</td>
                            <td className="py-1.5 pr-2">
                              <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                                isError ? 'bg-red-200 text-red-800' :
                                hasWarning ? 'bg-yellow-200 text-yellow-800' :
                                isUpdated ? 'bg-blue-200 text-blue-800' :
                                'bg-green-200 text-green-800'
                              }`}>
                                {isError ? 'error' : hasWarning ? 'aviso' : isUpdated ? 'actualizado' : '✓'}
                              </span>
                            </td>
                            <td className="py-1.5 pr-2">{row.name || '—'}</td>
                            <td className="py-1.5 pr-2 text-[var(--muted-foreground)]">{row.category || '—'}</td>
                            <td className="py-1.5 text-[var(--muted-foreground)]">
                              {hasWarning && row.warnings.map((w, wi) => (
                                <div key={wi} className="text-yellow-700">⚠ {w}</div>
                              ))}
                              {row.error && <div className="text-red-700">✗ {row.error}</div>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {applyLogFilter(importLog.rows, logFilter).length === 0 && (
                    <p className="text-center text-gray-400 py-8">No hay filas con este filtro.</p>
                  )}
                </div>

                <div className="p-4 border-t border-gray-100 flex items-center justify-between gap-3 flex-wrap">
                  <span className="text-xs text-gray-400">
                    {applyLogFilter(importLog.rows, logFilter).length} fila{applyLogFilter(importLog.rows, logFilter).length !== 1 ? 's' : ''} mostradas
                  </span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => downloadFilteredCSV(importLog, logFilter)}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-semibold transition-colors"
                    >
                      ⬇ Descargar CSV{logFilter !== 'all' ? ` (${({ created: 'creados', updated: 'actualizados', warning: 'avisos', error: 'errores' })[logFilter]})` : ''}
                    </button>
                    <a
                      href={`data:application/json;charset=utf-8,${encodeURIComponent(JSON.stringify(importLog, null, 2))}`}
                      download={`import-log-${importLog.log_id}.json`}
                      className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded text-sm font-semibold transition-colors"
                    >
                      ⬇ JSON
                    </a>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal para Eliminar Categoría */}
      {showDeleteCategoryModal && (
        <CategoryDeleteModal
          active={showDeleteCategoryModal}
          onClose={() => {
            setShowDeleteCategoryModal(false);
            setCategoryToDelete(null);
          }}
          onDelete={(category) => {
            handleDeleteCategory(category);
          }}
          categoryToDelete={categoryToDelete}
          setCategoryToDelete={setCategoryToDelete}
          token={token}
          venueID={venueID}
        />
      )}

      {/* Las cinco confirmaciones de la pantalla, con el diálogo de la app. */}
      <ConfirmDialog
        active={!!confirmacion}
        title={confirmacion?.title || ''}
        description={confirmacion?.description || null}
        note={confirmacion?.note || null}
        tone={confirmacion?.tone || 'default'}
        confirmLabel={confirmacion?.confirmLabel || 'Aceptar'}
        cancelLabel={confirmacion?.cancelLabel || 'Cancelar'}
        onConfirm={() => {
          const pendiente = confirmacion;
          setConfirmacion(null);
          pendiente?.accion?.();
        }}
        onCancel={() => setConfirmacion(null)}
      />
    </div>
  );
};

const CategoryEditModal = ({ active, onClose, onSave, categoryToEdit, setCategoryToEdit, token, venueID }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nextName, setNextName] = useState("");

  useEffect(() => {
    if (!active || !token || !venueID) {
      return;
    }

    const fetchCategories = async () => {
      try {
        const { data } = await axios.get('/v1/categories', {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(venueID && { 'x-venue-id': venueID })
          }
        });
        setCategories(data);
      } catch (err) {
        console.error('Error fetching categories:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [active, token, venueID]);

  useEffect(() => {
    setNextName(categoryToEdit?.name || "");
  }, [categoryToEdit]);

  if (!active) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">Editar Categoría</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-800 text-2xl font-bold"
            >
              ×
            </button>
          </div>
          {loading ? (
            <p className="text-gray-600">Cargando categorías...</p>
          ) : categories.length === 0 ? (
            <p className="text-gray-600">No hay categorías para editar</p>
          ) : (
            <>
              <p className="text-gray-600 mb-4">Selecciona la categoría que deseas renombrar:</p>
              <div className="max-h-48 overflow-y-auto mb-4 space-y-2">
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setCategoryToEdit(cat)}
                    className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all ${categoryToEdit?.id === cat.id
                        ? 'bg-blue-50 border-blue-500 text-blue-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-blue-300'
                      }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
              <input
                type="text"
                value={nextName}
                onChange={(e) => setNextName(e.target.value)}
                placeholder="Nuevo nombre"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none mb-4"
                disabled={!categoryToEdit}
              />
            </>
          )}
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-all"
            >
              Cancelar
            </button>
            <button
              onClick={() => onSave(categoryToEdit, nextName)}
              disabled={!categoryToEdit || !nextName.trim() || loading}
              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              Guardar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Componente modal para eliminar categoría
const CategoryDeleteModal = ({ active, onClose, onDelete, categoryToDelete, setCategoryToDelete, token, venueID }) => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (active && token && venueID) {
      const fetchCategories = async () => {
        try {
          const { data } = await axios.get('/v1/categories', {
            headers: {
              Authorization: `Bearer ${token}`,
              ...(venueID && { 'x-venue-id': venueID })
            }
          });
          setCategories(data);
          setLoading(false);
        } catch (err) {
          console.error('Error fetching categories:', err);
          setLoading(false);
        }
      };
      fetchCategories();
    }
  }, [active, token, venueID]);

  if (!active) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800">Eliminar Categoría</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-800 text-2xl font-bold"
            >
              ×
            </button>
          </div>
          {loading ? (
            <p className="text-gray-600">Cargando categorías...</p>
          ) : categories.length === 0 ? (
            <p className="text-gray-600">No hay categorías para eliminar</p>
          ) : (
            <>
              <p className="text-gray-600 mb-4">Selecciona la categoría que deseas eliminar:</p>
              <div className="max-h-60 overflow-y-auto mb-4 space-y-2">
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => setCategoryToDelete(cat)}
                    className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all ${categoryToDelete?.id === cat.id
                        ? 'bg-red-50 border-red-500 text-red-700'
                        : 'bg-white border-gray-300 text-gray-700 hover:border-red-300'
                      }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </>
          )}
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="px-5 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-all"
            >
              Cancelar
            </button>
            <button
              onClick={() => {
                if (categoryToDelete) {
                  onDelete(categoryToDelete);
                }
              }}
              disabled={!categoryToDelete || loading}
              className="px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              Eliminar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Product;
